import { useEffect, useRef, useState } from 'react';
import dagre from 'dagre';

/**
 * LayerExecutionCanvas - Specialized canvas for Layer Execution graphs
 *
 * This component handles MXNet-format graphs where nodes have an "inputs" array
 * that defines dependencies. Unlike the regular GraphCanvas, this builds edges
 * from the node.inputs references.
 */
export default function LayerExecutionCanvas({ graph, onNodeSelect }) {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(0.8);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [layoutGraph, setLayoutGraph] = useState(null);
  const [showNames, setShowNames] = useState(true);
  const [processedGraph, setProcessedGraph] = useState(null);
  const [graphBounds, setGraphBounds] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedNodes, setHighlightedNodes] = useState(new Set());

  // Process the MXNet graph to extract nodes and build edges from inputs
  useEffect(() => {
    if (!graph || !graph.nodes || graph.nodes.length === 0) return;

    console.log('[LayerExecutionCanvas] Processing MXNet graph:', {
      totalNodes: graph.nodes.length,
      argNodes: graph.arg_nodes?.length || 0,
      heads: graph.heads?.length || 0
    });

    // Build index to node mapping
    const indexToNode = new Map();
    graph.nodes.forEach((node, index) => {
      indexToNode.set(index, node);
    });

    // Process nodes and build edges from inputs
    const processedNodes = [];
    const edges = [];

    graph.nodes.forEach((node, index) => {
      // Extract display information
      const varName = node.name || `node_${index}`;
      const fnName = node.attrs?.fn_name || 'unknown';
      const opType = node.op || 'null';

      const processedNode = {
        name: varName,
        fnName: fnName,
        type: opType,
        operator: opType,
        index: index
      };

      processedNodes.push(processedNode);

      // Build edges from inputs array
      if (Array.isArray(node.inputs) && node.inputs.length > 0) {
        node.inputs.forEach(input => {
          // MXNet format: [nodeIndex, outputIndex, version]
          const fromIndex = Array.isArray(input) ? input[0] : input;
          if (fromIndex != null && indexToNode.has(fromIndex)) {
            const fromNode = indexToNode.get(fromIndex);
            const fromName = fromNode.name || `node_${fromIndex}`;
            const toName = varName;

            if (fromName !== toName) {
              edges.push({
                from: fromName,
                to: toName,
                fromIndex: fromIndex,
                toIndex: index
              });
            }
          }
        });
      }
    });

    console.log('[LayerExecutionCanvas] Processed:', {
      nodes: processedNodes.length,
      edges: edges.length,
      sampleEdges: edges.slice(0, 5)
    });

    setProcessedGraph({
      nodes: processedNodes,
      edges: edges
    });

  }, [graph]);

  // Filter nodes based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setHighlightedNodes(new Set());
      return;
    }

    const query = searchQuery.toLowerCase();
    const matches = new Set();
    
    if (processedGraph?.nodes) {
      processedGraph.nodes.forEach(node => {
        if (node.name.toLowerCase().includes(query) || 
            (node.fnName && node.fnName.toLowerCase().includes(query)) ||
            (node.type && node.type.toLowerCase().includes(query))) {
          matches.add(node.name);
        }
      });
    }
    
    setHighlightedNodes(matches);
  }, [searchQuery, processedGraph]);

  // Create and layout graph using dagre
  useEffect(() => {
    if (!processedGraph || !processedGraph.nodes || processedGraph.nodes.length === 0) return;

    try {
      // Create a new directed graph
      const g = new dagre.graphlib.Graph();

      // Set graph options
      g.setGraph({
        rankdir: 'TB', // Top to bottom
        nodesep: 50,   // Horizontal spacing
        ranksep: 80,   // Vertical spacing
        marginx: 20,
        marginy: 20
      });

      // Default node and edge labels
      g.setDefaultEdgeLabel(() => ({}));

      const nodeWidth = 200;
      const nodeHeight = 60;

      // Add nodes to graph
      processedGraph.nodes.forEach(node => {
        g.setNode(node.name, {
          label: node.name,
          width: nodeWidth,
          height: nodeHeight,
          node: node
        });
      });

      // Add edges from processed graph
      if (processedGraph.edges && processedGraph.edges.length > 0) {
        processedGraph.edges.forEach(({ from, to }) => {
          if (g.node(from) && g.node(to)) {
            g.setEdge(from, to);
          }
        });
      } else {
        // Fallback: connect sequentially if no edges
        for (let i = 0; i < processedGraph.nodes.length - 1; i++) {
          const from = processedGraph.nodes[i].name;
          const to = processedGraph.nodes[i + 1].name;
          g.setEdge(from, to);
        }
      }

      // Run dagre layout algorithm
      dagre.layout(g);

      // Calculate graph bounds for centering and pan restrictions
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      g.nodes().forEach(v => {
        const node = g.node(v);
        minX = Math.min(minX, node.x - node.width / 2);
        maxX = Math.max(maxX, node.x + node.width / 2);
        minY = Math.min(minY, node.y - node.height / 2);
        maxY = Math.max(maxY, node.y + node.height / 2);
      });

      const bounds = {
        minX,
        maxX,
        minY,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2
      };

      setGraphBounds(bounds);
      setLayoutGraph(g);

      console.log('[LayerExecutionCanvas] Laid out', g.nodeCount(), 'nodes and', g.edgeCount(), 'edges');
      console.log('[LayerExecutionCanvas] Graph bounds:', bounds);
    } catch (err) {
      console.error('[LayerExecutionCanvas] Dagre layout failed, falling back to grid layout:', err);

      // Fallback: simple grid layout
      const fallback = new dagre.graphlib.Graph();
      fallback.setGraph({});
      const nodeWidth = 200;
      const nodeHeight = 60;
      const cols = 8;
      const hGap = 40;
      const vGap = 60;
      processedGraph.nodes.forEach((node, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x = col * (nodeWidth + hGap) + nodeWidth / 2;
        const y = row * (nodeHeight + vGap) + nodeHeight / 2;
        const label = {
          label: node.name,
          width: nodeWidth,
          height: nodeHeight,
          node: node,
          x,
          y
        };
        fallback.setNode(node.name, label);
      });
      if (processedGraph.edges && processedGraph.edges.length > 0) {
        processedGraph.edges.forEach(({ from, to }) => {
          if (fallback.node(from) && fallback.node(to)) {
            fallback.setEdge(from, to);
          }
        });
      }
      setLayoutGraph(fallback);
    }
  }, [processedGraph]);

  // Render the graph
  useEffect(() => {
    if (!canvasRef.current || !layoutGraph) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Clear canvas with dark background
    ctx.fillStyle = '#404040';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate centered translation
    // Center the graph content in the canvas
    const centerX = graphBounds ? -graphBounds.centerX : 0;
    const centerY = graphBounds ? -graphBounds.centerY : 0;

    // Apply transformations
    ctx.save();
    ctx.translate(canvas.width / 2 + pan.x, canvas.height / 2 + pan.y);
    ctx.scale(zoom, zoom);
    ctx.translate(centerX, centerY);

    // Edge styling
    const edgeStroke = '#888';

    // Draw edges first
    layoutGraph.edges().forEach(e => {
      const edge = layoutGraph.edge(e);
      const sourceNode = layoutGraph.node(e.v);
      const targetNode = layoutGraph.node(e.w);

      ctx.strokeStyle = edgeStroke;
      ctx.lineWidth = 1;
      ctx.beginPath();

      // Use edge points if available, otherwise straight line
      if (edge.points && edge.points.length > 0) {
        ctx.moveTo(edge.points[0].x, edge.points[0].y);
        for (let i = 1; i < edge.points.length; i++) {
          ctx.lineTo(edge.points[i].x, edge.points[i].y);
        }
      } else {
        ctx.moveTo(sourceNode.x, sourceNode.y);
        ctx.lineTo(targetNode.x, targetNode.y);
      }

      ctx.stroke();

      // Draw arrow head
      const lastPoint = edge.points?.[edge.points.length - 1] || { x: targetNode.x, y: targetNode.y };
      const secondLast = edge.points?.[edge.points.length - 2] || { x: sourceNode.x, y: sourceNode.y };

      const angle = Math.atan2(lastPoint.y - secondLast.y, lastPoint.x - secondLast.x);
      const arrowSize = 8;

      ctx.fillStyle = edgeStroke;
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(
        lastPoint.x - arrowSize * Math.cos(angle - Math.PI / 6),
        lastPoint.y - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        lastPoint.x - arrowSize * Math.cos(angle + Math.PI / 6),
        lastPoint.y - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    });

    // Draw nodes
    layoutGraph.nodes().forEach(v => {
      const node = layoutGraph.node(v);
      const nodeData = node.node || { name: String(v), type: 'unknown', fnName: 'unknown' };

      // Check if this node is selected or highlighted by search
      const isSelected = selectedNode && selectedNode.name === nodeData.name;
      const isHighlighted = highlightedNodes.has(nodeData.name);

      // Prepare text content
      const fnName = nodeData.fnName || 'unknown';
      const opType = nodeData.type || nodeData.operator || 'null';

      // Determine what to show as main/sub headers
      // Main label: op value
      // Sublabel: "fn_name=<fn_name>" (when showNames is true)
      let mainHeader, subHeader;

      mainHeader = opType;

      if (showNames && fnName) {
        // Format: "fn_name=<function_name>"
        const fnDisplay = fnName.length > 24 ? fnName.substring(0, 21) + '...' : fnName;
        subHeader = `fn_name=${fnDisplay}`;
      } else {
        subHeader = null;
      }

      // Adjust band size based on whether we have subtitle
      const bandPaddingX = 16; // Increased padding for text overflow protection
      const bandHeight = subHeader ? 46 : 22; // Slightly taller for divider
      const bandRadius = 6;
      const bandY = node.y - 8;
      const bandX = node.x - (node.width / 2) + bandPaddingX;
      const bandWidth = node.width - bandPaddingX * 2;

      // Draw type band behind header(s)
      ctx.beginPath();
      ctx.moveTo(bandX + bandRadius, bandY - bandHeight / 2);
      ctx.lineTo(bandX + bandWidth - bandRadius, bandY - bandHeight / 2);
      ctx.quadraticCurveTo(bandX + bandWidth, bandY - bandHeight / 2, bandX + bandWidth, bandY - bandHeight / 2 + bandRadius);
      ctx.lineTo(bandX + bandWidth, bandY + bandHeight / 2 - bandRadius);
      ctx.quadraticCurveTo(bandX + bandWidth, bandY + bandHeight / 2, bandX + bandWidth - bandRadius, bandY + bandHeight / 2);
      ctx.lineTo(bandX + bandRadius, bandY + bandHeight / 2);
      ctx.quadraticCurveTo(bandX, bandY + bandHeight / 2, bandX, bandY + bandHeight / 2 - bandRadius);
      ctx.lineTo(bandX, bandY - bandHeight / 2 + bandRadius);
      ctx.quadraticCurveTo(bandX, bandY - bandHeight / 2, bandX + bandRadius, bandY - bandHeight / 2);
      ctx.closePath();
      ctx.fillStyle = isHighlighted ? '#3b82f6' : '#303030';
      ctx.fill();

      // Draw colored outline if selected or highlighted
      if (isSelected) {
        ctx.strokeStyle = '#ef4444'; // red-500
        ctx.lineWidth = 3;
        ctx.stroke();
      } else if (isHighlighted) {
        ctx.strokeStyle = '#3b82f6'; // blue-500
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Draw text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (subHeader) {
        // Show both main header and subtitle within the band

        // Main header
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        // Measure text to ensure it fits with padding
        const mainTextMetrics = ctx.measureText(mainHeader);
        const mainTextWidth = mainTextMetrics.width;
        const maxTextWidth = bandWidth - 8; // Leave some padding

        if (mainTextWidth > maxTextWidth) {
          // Scale down if text is too wide
          const scale = maxTextWidth / mainTextWidth;
          ctx.save();
          ctx.translate(node.x, node.y - 17);
          ctx.scale(scale, 1);
          ctx.fillText(mainHeader, 0, 0);
          ctx.restore();
        } else {
          ctx.fillText(mainHeader, node.x, node.y - 17);
        }

        // Draw divider line between main and sublabel
        const dividerY = node.y - 3;
        const dividerLength = bandWidth * 0.8; // 80% of band width
        ctx.strokeStyle = '#505050'; // Slightly lighter than background
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(node.x - dividerLength / 2, dividerY);
        ctx.lineTo(node.x + dividerLength / 2, dividerY);
        ctx.stroke();

        // Subtitle
        ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillStyle = '#e0e0e0';
        const subTextMetrics = ctx.measureText(subHeader);
        const subTextWidth = subTextMetrics.width;

        if (subTextWidth > maxTextWidth) {
          // Scale down if text is too wide
          const scale = maxTextWidth / subTextWidth;
          ctx.save();
          ctx.translate(node.x, node.y + 9);
          ctx.scale(scale, 1);
          ctx.fillText(subHeader, 0, 0);
          ctx.restore();
        } else {
          ctx.fillText(subHeader, node.x, node.y + 9);
        }
      } else {
        // Just show main header centered
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

        // Measure and scale if needed
        const textMetrics = ctx.measureText(mainHeader);
        const textWidth = textMetrics.width;
        const maxTextWidth = bandWidth - 8;

        if (textWidth > maxTextWidth) {
          const scale = maxTextWidth / textWidth;
          ctx.save();
          ctx.translate(node.x, node.y - 8);
          ctx.scale(scale, 1);
          ctx.fillText(mainHeader, 0, 0);
          ctx.restore();
        } else {
          ctx.fillText(mainHeader, node.x, node.y - 8);
        }
      }
    });

    ctx.restore();

    // Draw zoom level
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Zoom: ${(zoom * 100).toFixed(0)}%`, 10, canvas.height - 10);

  }, [layoutGraph, zoom, pan, showNames, graphBounds, selectedNode, highlightedNodes]);

  // Helper function to constrain pan within graph bounds
  const constrainPan = (newPan) => {
    if (!graphBounds || !canvasRef.current) return newPan;

    const scaledWidth = graphBounds.width * zoom;
    const scaledHeight = graphBounds.height * zoom;

    // Add padding
    const padding = 100;

    // Calculate max pan limits to keep content visible
    const maxPanX = scaledWidth / 2 + padding;
    const maxPanY = scaledHeight / 2 + padding;
    const minPanX = -scaledWidth / 2 - padding;
    const minPanY = -scaledHeight / 2 - padding;

    return {
      x: Math.max(minPanX, Math.min(maxPanX, newPan.x)),
      y: Math.max(minPanY, Math.min(maxPanY, newPan.y))
    };
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(3, prev * delta)));
  };

  // Helper function to check if a point is inside a node
  const getNodeAtPosition = (canvasX, canvasY) => {
    if (!layoutGraph || !canvasRef.current || !graphBounds) return null;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Convert canvas coordinates to graph coordinates
    const centerX = graphBounds ? -graphBounds.centerX : 0;
    const centerY = graphBounds ? -graphBounds.centerY : 0;

    // Reverse the transformation
    const graphX = (canvasX - canvas.width / 2 - pan.x) / zoom - centerX;
    const graphY = (canvasY - canvas.height / 2 - pan.y) / zoom - centerY;

    // Check each node
    for (const v of layoutGraph.nodes()) {
      const node = layoutGraph.node(v);
      const halfWidth = node.width / 2;
      const halfHeight = node.height / 2;

      if (
        graphX >= node.x - halfWidth &&
        graphX <= node.x + halfWidth &&
        graphY >= node.y - halfHeight &&
        graphY <= node.y + halfHeight
      ) {
        return node;
      }
    }

    return null;
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };
    setPan(constrainPan(newPan));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;

    const clickedNode = getNodeAtPosition(canvasX, canvasY);

    if (clickedNode) {
      // Find the original graph node data
      const originalNode = graph.nodes.find(n => n.name === clickedNode.node.name);
      setSelectedNode(originalNode);
      setSidebarOpen(true);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(3, prev * 1.2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.1, prev / 1.2));
  };

  const handleReset = () => {
    setZoom(0.8);
    setPan({ x: 0, y: 0 });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setHighlightedNodes(new Set());
  };

  const handleJumpToLayer = () => {
    if (selectedNode && onNodeSelect) {
      onNodeSelect(selectedNode);
    }
  };

  if (!processedGraph) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-800">
        <div className="text-gray-400">Processing graph...</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {/* Search Bar */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 w-96">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search nodes by name or function..."
            className="w-full px-4 py-2 pr-20 bg-white border border-gray-300 rounded-lg shadow-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              title="Clear search"
            >
              ✕
            </button>
          )}
          {highlightedNodes.size > 0 && (
            <div className="absolute right-10 top-1/2 transform -translate-y-1/2 text-xs text-blue-600 font-medium">
              {highlightedNodes.size} found
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col items-stretch gap-3">
        <label className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm shadow-lg cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-blue-600"
            checked={showNames}
            onChange={(e) => setShowNames(e.target.checked)}
          />
          <span className="text-gray-700">Show function names</span>
        </label>
        <button
          onClick={handleZoomIn}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Zoom In"
        >
          ➕
        </button>
        <button
          onClick={handleZoomOut}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Zoom Out"
        >
          ➖
        </button>
        <button
          onClick={handleReset}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Reset View"
        >
          ⟲
        </button>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-move bg-gray-800"
        style={{ height: '100%' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleCanvasClick}
      />


      {/* Stats */}
      <div className="absolute top-4 left-4 text-sm text-gray-300 bg-gray-800/95 px-4 py-3 rounded-lg shadow-lg border border-gray-700 backdrop-blur">
        <div className="font-bold mb-2 text-base">Layer Execution</div>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-200 border-2 border-gray-500 rounded"></span>
            <span className="font-medium">Nodes:</span>
            <span className="text-gray-400">{processedGraph?.nodes?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-100 border-2 border-gray-400 rounded"></span>
            <span className="font-medium">Edges:</span>
            <span className="text-gray-400">{processedGraph?.edges?.length || 0}</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-500">
          Layout: Dagre
        </div>
      </div>

      {/* Node Properties Sidebar */}
      {sidebarOpen && selectedNode && (
        <div className="absolute top-0 right-0 h-full w-80 bg-[#2b2b2b] border-l border-gray-700 shadow-2xl z-20 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-[#1e1e1e]">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wide">Node Properties</h3>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Jump to Layer Button */}
          {onNodeSelect && (
            <div className="px-4 py-3 border-b border-gray-700 bg-[#252525]">
              <button
                onClick={handleJumpToLayer}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <span>→</span>
                Jump to Layer
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Type */}
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Type</div>
              <div className="bg-[#3a3a3a] px-3 py-2 rounded text-sm text-gray-200 font-mono">
                {selectedNode.op || 'null'}
              </div>
            </div>

            {/* Name */}
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Name</div>
              <div className="bg-[#3a3a3a] px-3 py-2 rounded text-sm text-gray-200 font-mono">
                {selectedNode.name || 'unnamed'}
              </div>
            </div>

            {/* Attributes */}
            {selectedNode.attrs && Object.keys(selectedNode.attrs).length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Attributes</div>
                <div className="space-y-2">
                  {Object.entries(selectedNode.attrs).map(([key, value]) => (
                    <div key={key} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{key}</div>
                      <div className="text-sm text-gray-200 font-mono break-all">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(typeof value === 'object' ? JSON.stringify(value) : String(value))}
                        className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                        title="Copy to clipboard"
                      >
                        ➕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inputs */}
            {selectedNode.inputs && selectedNode.inputs.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Inputs</div>
                <div className="space-y-2">
                  {selectedNode.inputs.map((input, idx) => (
                    <div key={idx} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{idx}</div>
                      <div className="text-sm text-gray-200 font-mono">
                        name: [{Array.isArray(input) ? input.join(', ') : input}]
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outputs */}
            {selectedNode.outputs && selectedNode.outputs.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Outputs</div>
                <div className="space-y-2">
                  {selectedNode.outputs.map((output, idx) => (
                    <div key={idx} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{idx}</div>
                      <div className="text-sm text-gray-200 font-mono">
                        name: [{Array.isArray(output) ? output.join(', ') : output}]
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
